import type { AgentActionRecord, AgentActionStatus, AgentRole, WorkerRole } from "./agents";

export interface WorkerTaskPlan {
  id: string;
  runId: string;
  agentRole: AgentRole;
  workerRole: WorkerRole;
  title: string;
  targetFileIds: string[];
  dependsOn: string[];
  mutatesState: boolean;
  expectedRevisionByFile?: Record<string, number>;
}

export interface WorkerPlanValidation {
  ok: boolean;
  errors: string[];
}

export interface WorkerBatch {
  ready: WorkerTaskPlan[];
  waiting: WorkerTaskPlan[];
  blocked: { task: WorkerTaskPlan; reason: string }[];
}

const terminalStatuses = new Set<AgentActionStatus>(["completed", "failed", "denied", "cancelled"]);
const allowedTransitions: Record<AgentActionStatus, AgentActionStatus[]> = {
  planned: ["running", "denied", "cancelled"],
  running: ["completed", "failed", "cancelled"],
  completed: [], failed: [], denied: [], cancelled: [],
};

/** Validates a proposed helper graph before any worker receives tools. */
export function validateWorkerPlan(tasks: readonly WorkerTaskPlan[], _maxWorkers = 8): WorkerPlanValidation {
  const errors: string[] = [];
  if (tasks.length > 256) errors.push("worker plan exceeds 256 tasks");
  const byId = new Map<string, WorkerTaskPlan>();
  for (const task of tasks) {
    if (!task.id.trim()) errors.push("worker task id is required");
    else if (byId.has(task.id)) errors.push(`duplicate worker task id: ${task.id}`);
    else byId.set(task.id, task);
    if (task.dependsOn.includes(task.id)) errors.push(`${task.id} cannot depend on itself`);
    if (task.mutatesState && task.targetFileIds.some((fileId) => task.expectedRevisionByFile?.[fileId] == null)) errors.push(`${task.id} must fence every target file revision`);
  }
  for (const task of tasks) for (const dependency of task.dependsOn) if (!byId.has(dependency)) errors.push(`${task.id} depends on missing task: ${dependency}`);

  const visiting = new Set<string>(); const visited = new Set<string>();
  const visit = (id: string): boolean => {
    if (visiting.has(id)) return true;
    if (visited.has(id)) return false;
    visiting.add(id);
    const cyclic = (byId.get(id)?.dependsOn ?? []).some(visit);
    visiting.delete(id); visited.add(id);
    return cyclic;
  };
  for (const id of byId.keys()) if (visit(id)) { errors.push(`worker plan contains a dependency cycle at ${id}`); break; }
  return { ok: errors.length === 0, errors };
}

/** Selects only dependency-safe work. A failed dependency blocks descendants. */
export function scheduleWorkerBatch(tasks: readonly WorkerTaskPlan[], actions: readonly AgentActionRecord[], maxConcurrency = 8): WorkerBatch {
  const actionById = new Map(actions.map((action) => [action.id, action]));
  const running = actions.filter((action) => action.status === "running").length;
  let slots = Math.max(0, Math.trunc(maxConcurrency) - running);
  const ready: WorkerTaskPlan[] = []; const waiting: WorkerTaskPlan[] = []; const blocked: WorkerBatch["blocked"] = [];
  for (const task of tasks) {
    const current = actionById.get(task.id);
    if (current && (current.status === "running" || terminalStatuses.has(current.status))) continue;
    const dependencies = task.dependsOn.map((id) => actionById.get(id));
    const failed = dependencies.find((dependency) => dependency && dependency.status !== "completed" && terminalStatuses.has(dependency.status));
    if (failed) { blocked.push({ task, reason: `dependency ${failed.id} ended as ${failed.status}` }); continue; }
    if (dependencies.some((dependency) => dependency?.status !== "completed")) { waiting.push(task); continue; }
    if (slots > 0) { ready.push(task); slots -= 1; } else waiting.push(task);
  }
  return { ready, waiting, blocked };
}

/** Applies the same state machine in browser, Worker and database adapters. */
export function transitionAgentAction(action: AgentActionRecord, status: AgentActionStatus, at: string, detail: { summary?: string; error?: string; changedFieldCount?: number } = {}): AgentActionRecord {
  if (!allowedTransitions[action.status].includes(status)) throw new Error(`invalid agent action transition: ${action.status} -> ${status}`);
  return {
    ...action, status,
    ...(detail.summary == null ? {} : { summary: detail.summary }),
    ...(detail.error == null ? {} : { error: detail.error }),
    ...(detail.changedFieldCount == null ? {} : { changedFieldCount: Math.max(0, Math.trunc(detail.changedFieldCount)) }),
    ...(terminalStatuses.has(status) ? { completedAt: at } : {}),
  };
}
